import { useCallback, useState } from 'react';
import reorderPhaseIds from '~/utils/reorder-phases';

type UsePhaseDragReorderParams = {
  phaseIds: string[];
  isPreview: boolean;
  onReorder: (orderedIds: string[]) => void | Promise<void>;
};

const usePhaseDragReorder = ({
  phaseIds,
  isPreview,
  onReorder,
}: UsePhaseDragReorderParams) => {
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  const handleDragStart = useCallback((phaseId: string) => (
    nativeEvent: React.DragEvent<HTMLElement>,
  ) => {
    const transfer = nativeEvent.dataTransfer;
    if (transfer) {
      transfer.effectAllowed = 'move';
      transfer.setData('text/plain', phaseId);
    }
    setDraggingId(phaseId);
  }, []);

  const handleDragOver = useCallback((phaseId: string) => (
    nativeEvent: React.DragEvent<HTMLElement>,
  ) => {
    nativeEvent.preventDefault();
    const transfer = nativeEvent.dataTransfer;
    if (transfer) {
      transfer.dropEffect = 'move';
    }
    setDragOverId(phaseId);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverId(null);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggingId(null);
    setDragOverId(null);
  }, []);

  const handleDrop = useCallback((targetId: string) => async (
    nativeEvent: React.DragEvent<HTMLElement>,
  ) => {
    nativeEvent.preventDefault();
    const sourceId = nativeEvent.dataTransfer.getData('text/plain') || draggingId;
    setDraggingId(null);
    setDragOverId(null);
    if (!sourceId) {
      return;
    }
    const nextIds = reorderPhaseIds(phaseIds, sourceId, targetId);
    if (nextIds) {
      await onReorder(nextIds);
    }
  }, [draggingId, onReorder, phaseIds]);

  const getTabDragProps = useCallback((phaseId: string) => {
    if (isPreview) {
      return {};
    }
    return {
      draggable: true,
      onDragStart: handleDragStart(phaseId),
      onDragOver: handleDragOver(phaseId),
      onDragLeave: handleDragLeave,
      onDragEnd: handleDragEnd,
      onDrop: handleDrop(phaseId),
    };
  }, [
    handleDragEnd,
    handleDragLeave,
    handleDragOver,
    handleDragStart,
    handleDrop,
    isPreview,
  ]);

  const getTabDragStateClass = useCallback((
    phaseId: string,
    draggingClass: string,
    overClass: string,
  ) => {
    if (draggingId === phaseId) {
      return draggingClass;
    }
    if (dragOverId === phaseId && draggingId !== phaseId) {
      return overClass;
    }
    return '';
  }, [dragOverId, draggingId]);

  return {
    getTabDragProps,
    getTabDragStateClass,
  };
};

export default usePhaseDragReorder;
